import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { api, API } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Megaphone, ArrowRight, Star } from "lucide-react";

export const fmtAnnDate = (iso) =>
  iso ? new Date(`${iso}T12:00:00`).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : "";

export const News = () => {
  const [items, setItems] = useState([]);
  const [reading, setReading] = useState(null);

  useEffect(() => {
    api.get("/announcements").then(({ data }) => setItems(data)).catch(() => {});
  }, []);

  if (!items.length) return null;
  const a = items[0];

  return (
    <section data-testid="news-section" aria-label="News and announcements" className="relative bg-black pt-6 pb-2 sm:pt-8">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          data-testid={`news-card-${a.announcement_id}`}
          className={`relative flex flex-col md:flex-row overflow-hidden border ${a.featured ? "border-neon shadow-[0_0_30px_rgba(255,45,143,0.25)]" : "border-neutral-800"} bg-[#0A0A0A]`}
        >
          {a.has_image && (
            <div className="md:w-72 lg:w-80 shrink-0 h-44 md:h-auto overflow-hidden border-b md:border-b-0 md:border-r border-neutral-900">
              <img src={`${API}/announcements/${a.announcement_id}/image?v=${encodeURIComponent(a.image_v)}`}
                alt="" className="w-full h-full object-cover" />
            </div>
          )}
          <div className="flex-1 p-5 sm:p-7">
            <div className="flex flex-wrap items-center gap-3 mb-3">
              <span className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-neon">
                <Megaphone size={13} aria-hidden="true" /> News &amp; Announcements
              </span>
              {a.featured && (
                <span data-testid="news-featured-badge" className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-neon text-black font-semibold px-2 py-0.5">
                  <Star size={10} aria-hidden="true" /> Featured
                </span>
              )}
              <span className="text-xs text-neutral-500">{fmtAnnDate(a.date)}</span>
            </div>
            <h2 data-testid="news-title" className="font-display uppercase text-2xl sm:text-3xl leading-none mb-2">{a.title}</h2>
            <p className="text-sm text-neutral-400 leading-relaxed line-clamp-2 max-w-3xl">{a.description}</p>
            <div className="mt-4 flex flex-wrap items-center gap-4">
              <button onClick={() => setReading(a)} data-testid="news-read-more-btn"
                className="inline-flex items-center gap-2 bg-neon text-black font-semibold text-xs uppercase tracking-[0.2em] px-5 py-3 hover:bg-white transition-colors">
                Read more <ArrowRight size={13} aria-hidden="true" />
              </button>
              <Link to="/news" data-testid="news-view-all-link"
                className="text-xs uppercase tracking-[0.2em] text-neutral-400 hover:text-neon transition-colors underline underline-offset-4">
                View all news
              </Link>
            </div>
          </div>
        </motion.div>
      </div>

      <Dialog open={!!reading} onOpenChange={(o) => !o && setReading(null)}>
        <DialogContent data-testid="news-read-dialog" className="bg-[#0A0A0A] border-neutral-800 text-white max-w-lg max-h-[85vh] overflow-y-auto">
          {reading && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display uppercase text-3xl leading-none">{reading.title}</DialogTitle>
                <DialogDescription className="text-neutral-500 text-xs uppercase tracking-wider">{fmtAnnDate(reading.date)}</DialogDescription>
              </DialogHeader>
              {reading.has_image && (
                <img src={`${API}/announcements/${reading.announcement_id}/image?v=${encodeURIComponent(reading.image_v)}`}
                  alt="" className="w-full max-h-64 object-cover border border-neutral-900" />
              )}
              <p className="text-sm text-neutral-300 leading-relaxed whitespace-pre-wrap">{reading.description}</p>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
